<template>
  <v-layout row class="fab-container">

    <nx-round-button
      :disabled="disabled"
      @click.native="$emit('click:preview')"
      color="grey"
      icon="visibility"
      v-if="hasPreview"
    ></nx-round-button>

    <nx-round-button v-if="isDirty && !hasScheduledPublication"
                     :color="saveColor"
                     :disabled="disabled"
                     :error="numErrors"
                     :loading="loading"
                     @click.native="save"
                     icon="save"
    ></nx-round-button>
    <nx-round-button v-if="allowPublish && isPublishable && draft.isDraft && !isDirty && !hasScheduledPublication"
                     :color="publishColor"
                     :disabled="disabled"
                     :error="numErrors"
                     :loading="loading"
                     @click.native="publish"
                     icon="publish"
    ></nx-round-button>
    <nx-round-button v-else-if="allowPublish && isPublishable && isDirty && hasScheduledPublication"
                     :color="publishColor"
                     :disabled="disabled"
                     :error="numErrors"
                     :loading="loading"
                     @click.native="schedule"
                     icon="alarm"
    ></nx-round-button>

    <v-tooltip bottom v-if="canClone">
      <v-fab-transition slot="activator">
        <v-btn fab
               color="primary"
               @click="cloneCurrentVersion"
               :loading="loading">
          <v-icon>control_point_duplicate</v-icon>
        </v-btn>
      </v-fab-transition>
      <span>{{ $t('message.entity.actions.clone') }}</span>
    </v-tooltip>
  </v-layout>
</template>

<script>
import Storable from '@/mixins/storable';
import NxRoundButton from '@/components/buttons/RoundButton';
import { isEmpty } from 'lodash';

export default {
  name: 'nx-entity-editor-buttons',
  mixins: [Storable],
  components: {
    NxRoundButton,
  },
  props: {
    editorStorePath: {
      type: [String, Array],
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    form: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
    },
    allowPublish: {
      type: Boolean,
      default: () => true,
    },
    hasPreview: {
      type: Boolean,
      default: () => false,
    },
  },
  computed: {
    storePath() {
      return this.editorStorePath;
    },
    draft() {
      return this.state.draft;
    },
    errors() {
      return this.state.errors;
    },
    relativeVersionId() {
      return this.draft.relativeVersionId;
    },
    isDirty() {
      return this.getters.isDirty;
    },
    isPublishable() {
      return this.getters.isPublishable;
    },
    isPublished() {
      return this.draft.published;
    },
    hasEverBeenPublished() {
      return this.draft.hasEverBeenPublished === true;
    },
    hasScheduledPublication() {
      return this.getters.hasScheduledPublication;
    },
    hasErrors() {
      return this.errors.length > 0;
    },
    hasRequiredForPublishErrors() {
      if (isEmpty(this.form)) {
        return false;
      }
      return this.form.validForPublish !== true;
    },
    numErrors() {
      return this.errors.length;
    },
    saveColor() {
      return this.hasErrors ? 'error' : 'accent';
    },
    publishColor() {
      return this.hasErrors || this.hasRequiredForPublishErrors ? 'error' : 'accent';
    },
    canClone() {
      if (!this.relativeVersionId) {
        // Clone only works with versions
        return false;
      }
      return !this.disabled && (this.isPublished || this.hasEverBeenPublished);
    },
  },
  methods: {
    save() {
      if (this.hasErrors) {
        this.$emit('validate');
        return;
      }
      this.$emit('save');
    },
    schedule() {
      this.save();
    },
    async cloneCurrentVersion() {
      this.$emit('clone');
    },
    publish() {
      if (this.hasRequiredForPublishErrors) {
        this.$emit('validate:publish');
        return;
      }
      if (this.hasErrors) {
        this.$emit('validate');
        return;
      }
      this.$emit('publish');
    },
    onKeyDown(event) {
      if (event.ctrlKey || event.metaKey) {
        switch (String.fromCharCode(event.which).toLowerCase()) {
          // If ctrl+s was pressed...
          case 's':
            event.preventDefault();
            if (!this.disabled && this.isDirty && !this.hasScheduledPublication) {
              this.save();
            }
            break;
          default:
            // None by default
        }
      }
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeyDown);
  },
};
</script>

<style lang="stylus" scoped>
.fab-container {
  position: absolute;
  bottom: -32px;
  right: 0;
}
</style>
